export const DEPOSIT_OUTCOME = {
  // The customer left the payment page: nothing is known, or being confirmed, until the cancel has been answered.
  checking: 'checking',
  processing: 'processing',
  confirming: 'confirming',
  // Not known how the payment stands: the server refused the question (asking again would not help), or a cancel
  // never got through to it.
  unconfirmed: 'unconfirmed',
  booked: 'booked',
  // Fell back: the request is saved and no money was taken.
  notPaid: 'notPaid',
  // The customer was charged and the money is going back.
  refunded: 'refunded',
  // An old page whose booking was changed: nothing was saved for its token.
  replaced: 'replaced',
};

/** The server's word for how a deposit stands, as the page tells it. */
export const DEPOSIT_STATUS_OUTCOME = {
  Processing: DEPOSIT_OUTCOME.processing,
  Booked: DEPOSIT_OUTCOME.booked,
  NotPaid: DEPOSIT_OUTCOME.notPaid,
  Refunded: DEPOSIT_OUTCOME.refunded,
  Replaced: DEPOSIT_OUTCOME.replaced,
};

/**
 * About four minutes of looking before the page stops insisting: every four seconds for the first minute (the webhook
 * books a payment within seconds), then every ten, as a payment found by a customer's own request waits for the sweep,
 * which runs every three minutes. The status call has a rate limit, so a 429 doubles the wait (up to maxIntervalMs)
 * instead of asking again at the same pace.
 */
export const DEPOSIT_POLL = {
  intervalMs: 4000,
  slowAfterMs: 60000,
  slowIntervalMs: 10000,
  maxIntervalMs: 20000,
  totalMs: 240000,
  cancelTries: 3,
  cancelPauseMs: 1500,
};

/**
 * The payment page sends the customer back to the portal with #deposit=success|cancel&token=... in the fragment, which
 * never reaches a server or a proxy log. The portal's index route redirects and would drop it, so the app's entry takes
 * it off the address first (handOverDepositReturn) and keeps it for the booking screen.
 */
export const parseDepositReturn = (hash) => {
  if (typeof hash !== 'string' || !hash.startsWith('#')) return null;

  const params = new URLSearchParams(hash.slice(1));
  const kind = params.get('deposit');
  const token = params.get('token');

  return ['success', 'cancel'].includes(kind) && token ? { kind, token } : null;
};

const isReturnPart = (part) => ['deposit', 'token', 'company'].includes(part.split('=')[0]);

/** The fragment without the way back, the rest of it kept exactly as it was written. */
export const withoutDepositReturn = (hash) => {
  const rest = (hash || '').replace(/^#/, '').split('&')
    .filter((part) => part && !isReturnPart(part))
    .join('&');

  return rest ? `#${rest}` : '';
};

/**
 * The form offers a deposit with the booking of a service at a time: there are services to price, a date and a time to
 * pick, and an amount to pay.
 */
export const isDepositOffered = (form) => !!form?.deposit?.isEnabled
  && form.deposit.amount > 0
  && form.services?.length > 0
  && !!form.showDateTimePicker;

/**
 * The deposit is taken with a picked service and time: a request with no time to hold, or no service to price, has
 * nothing to pay for (the services step can be skipped), and stays the plain request it always was.
 */
export const isDepositDue = (form, values) => !!form?.deposit?.isEnabled
  && form.deposit.amount > 0
  && values.serviceIds?.length > 0
  && !!values.date
  && !!values.time;

/** The submit says that a payment follows, so the customer is not sent to the payment page by a plain "Submit". */
export const getSubmitLabel = (form, values) => (isDepositDue(form, values) ? 'Submit and pay' : 'Submit');

/** The deposit to pay in the answer to a submit; the answer of a booking without one has none. */
export const getCheckout = (data) => (data?.deposit?.checkoutUrl ? data.deposit : null);

const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

/**
 * An address the page may send the customer to from the server's answer: https, and plain http only on this machine
 * (the return page of a local API). Anything else (javascript:, data:, a relative address) is refused.
 */
export const isSafePaymentUrl = (url) => {
  try {
    const { protocol, hostname } = new URL(url);

    return protocol === 'https:' || (protocol === 'http:' && LOCAL_HOSTS.includes(hostname));
  } catch {
    return false;
  }
};

const tokenKey = (portalId) => `booking-deposit-token:${portalId}`;

// A payment page runs 35 minutes and an unpaid one holds its slot about 12 more: then the token goes on with nothing.
const TOKEN_LIFETIME_MS = 50 * 60000;

const readKeptToken = (portalId) => {
  const { token, savedAt } = JSON.parse(window.localStorage.getItem(tokenKey(portalId))) ?? {};

  return typeof token === 'string' && token && Number.isFinite(savedAt) ? { token, savedAt } : null;
};

/**
 * The token of the deposit the customer was last sent to pay, kept in the browser for this portal: sent back with the
 * next submit, it is the only thing that lets the server give a customer who came back (with Back, or in another tab)
 * their own payment page again. Storage may be blocked; the booking then works without it.
 */
export const readDepositToken = (portalId) => {
  try {
    const kept = readKeptToken(portalId);

    if (kept && Date.now() - kept.savedAt > TOKEN_LIFETIME_MS) {
      window.localStorage.removeItem(tokenKey(portalId));

      return null;
    }

    return kept?.token ?? null;
  } catch {
    return null;
  }
};

export const keepDepositToken = (portalId, token) => {
  try {
    window.localStorage.setItem(tokenKey(portalId), JSON.stringify({ token, savedAt: Date.now() }));
  } catch {
    // Without storage a customer who comes back is treated like anyone else.
  }
};

/** Forgets it; with a token, only if that is the one kept (a newer deposit's is not ours to drop). */
export const forgetDepositToken = (portalId, token = null) => {
  try {
    if (token === null || readKeptToken(portalId)?.token === token) {
      window.localStorage.removeItem(tokenKey(portalId));
    }
  } catch {
    // Nothing was kept.
  }
};

/**
 * What the page keeps and forgets of the payment token on the server's answer to a submit, and where it goes next: to
 * the payment page, nowhere for an address it must not follow (`unsafe`), or nowhere at all for a booking that took no
 * deposit. Only the token that was sent may go with a no-deposit answer: another tab may have kept a newer one since.
 */
export const takeSubmitAnswer = (portalId, sentToken, data) => {
  const checkout = getCheckout(data);

  if (checkout) {
    // The draft exists whatever the page does with its answer: the token stays, so that another try finds it.
    keepDepositToken(portalId, checkout.token);

    return isSafePaymentUrl(checkout.checkoutUrl) ? { checkoutUrl: checkout.checkoutUrl } : { unsafe: true };
  }

  // This booking took no deposit: the payment page of an earlier try is given up, and its token is of no use.
  if (sentToken) forgetDepositToken(portalId, sentToken);

  return {};
};

const returnKey = (portalId) => `booking-deposit-return:${portalId}`;

// With storage blocked the way back is kept here, which is as long as this page load: enough for the booking screen to
// open on it, as the customer comes back to a page that starts the app afresh.
const arrived = new Map();

/** The way back, kept in this tab for this portal once it has been taken out of the address. */
export const readDepositReturn = (portalId) => {
  try {
    const { kind, token } = JSON.parse(window.sessionStorage.getItem(returnKey(portalId))) ?? {};

    if (['success', 'cancel'].includes(kind) && token) return { kind, token };
  } catch {
    // Unreadable: what this page load kept itself, if anything, is all there is.
  }

  return arrived.get(portalId) ?? null;
};

export const keepDepositReturn = (portalId, { kind, token }) => {
  try {
    window.sessionStorage.setItem(returnKey(portalId), JSON.stringify({ kind, token }));
  } catch {
    arrived.set(portalId, { kind, token });
  }
};

export const forgetDepositReturn = (portalId) => {
  arrived.delete(portalId);

  try {
    window.sessionStorage.removeItem(returnKey(portalId));
  } catch {
    // Nothing was kept.
  }
};

/**
 * Called once at the app's entry, before the router redirects the portal's home (a redirect drops the fragment): keeps
 * the way back for the booking screen and takes only it out of the address, so the token stays in no history entry.
 */
export const handOverDepositReturn = (win = window) => {
  try {
    const portalId = win.location.pathname.split('/').filter(Boolean)[0];
    const ret = portalId ? parseDepositReturn(win.location.hash) : null;

    if (!ret) return;

    keepDepositReturn(portalId, ret);
    win.history.replaceState(
      win.history.state,
      '',
      `${win.location.pathname}${win.location.search}${withoutDepositReturn(win.location.hash)}`,
    );
  } catch {
    // Left in the address: nothing here may stop the app from starting.
  }
};
